import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { StatusBadge } from "./StatusBadge";
import { toast } from "sonner";
import { formatDate, daysUntil, formatApiError } from "../lib/format";
import { TASK_STATUSES, TASK_STATUS_LABEL, TASK_PRIORITY_LABEL, DIVISIONS, isManagement } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import {
  AlertTriangle, CheckCircle2, Clock, Send, Undo2, Plus, X, Trash2, ShieldCheck, ShieldX,
} from "lucide-react";

// Verifier = Owner/GM/Admin/DivMgr/MUA Admin (matches backend _is_verifier)
const isVerifier = (user) =>
  !!user && (["owner", "admin", "general_manager", "division_manager"].includes(user.role)
    || (user.role === "division_admin" && user.division_id === "div-mua"));

export default function TaskDetailModal({ taskId, open, onOpenChange, onChanged }) {
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newItem, setNewItem] = useState("");
  const [blockerNote, setBlockerNote] = useState("");
  const [submitNote, setSubmitNote] = useState("");
  const [rejectNote, setRejectNote] = useState("");
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);

  const load = async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/tasks/${taskId}`);
      setTask(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail || "Gagal memuat")); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open && taskId) { load(); setNewItem(""); setBlockerNote(""); setSubmitNote(""); setRejectNote(""); setShowBlockForm(false); setShowRejectForm(false); } // eslint-disable-next-line
  }, [open, taskId]);

  const refresh = (data) => { setTask(data); onChanged && onChanged(); };

  const addItem = async () => {
    if (!newItem.trim()) return;
    try {
      const { data } = await api.post(`/tasks/${taskId}/checklist`, { text: newItem.trim() });
      refresh(data); setNewItem("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleItem = async (itemId, done) => {
    try { const { data } = await api.put(`/tasks/${taskId}/checklist/${itemId}`, { done }); refresh(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeItem = async (itemId) => {
    if (!window.confirm("Hapus item ceklis?")) return;
    try { const { data } = await api.delete(`/tasks/${taskId}/checklist/${itemId}`); refresh(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doSubmitDone = async () => {
    try {
      const { data } = await api.post(`/tasks/${taskId}/submit-done`, { note: submitNote });
      toast.success(task?.is_personal ? "Tugas ditandai selesai" : "Klaim selesai dikirim, menunggu verifikasi Manager");
      refresh(data); setSubmitNote("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doVerify = async () => {
    try {
      const { data } = await api.post(`/tasks/${taskId}/verify-done`, { note: "" });
      toast.success("Diverifikasi selesai"); refresh(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doReject = async () => {
    if (!rejectNote.trim()) return toast.error("Wajib isi alasan penolakan");
    try {
      const { data } = await api.post(`/tasks/${taskId}/reject-done`, { note: rejectNote.trim() });
      toast.success("Klaim selesai ditolak"); refresh(data); setShowRejectForm(false); setRejectNote("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doBlock = async () => {
    if (!blockerNote.trim()) return toast.error("Wajib isi keterangan kendala");
    try {
      const { data } = await api.post(`/tasks/${taskId}/block`, { note: blockerNote.trim() });
      toast.success("Kendala dikirim ke Manager"); refresh(data); setShowBlockForm(false); setBlockerNote("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doUnblock = async () => {
    try { const { data } = await api.post(`/tasks/${taskId}/unblock`, { note: "" }); toast.success("Kendala ditutup"); refresh(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const changeStatus = async (status) => {
    try { const { data } = await api.put(`/tasks/${taskId}`, { status }); refresh(data); toast.success("Status diperbarui"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const checklist = task?.checklist || [];
  const doneCount = checklist.filter((x) => x.done).length;
  const pct = checklist.length ? Math.round((100 * doneCount) / checklist.length) : (task?.progress_pct || 0);
  const div = DIVISIONS.find((x) => x.id === task?.division_id);
  const d = task ? daysUntil(task.due_date) : 0;
  const late = task && d < 0 && task.status !== "Done";
  const canVerify = isVerifier(user) && (user?.role !== "division_manager" || user?.division_id === task?.division_id);
  const canAct = task && (canVerify || task.is_personal || (user && user.division_id === task.division_id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="task-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span>{task?.title || (loading ? "Memuat…" : "Tugas")}</span>
            {task?.is_personal && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 border border-purple-200">PRIBADI</span>}
            {task?.blocked && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200 flex items-center gap-1"><AlertTriangle className="w-3 h-3"/>KENDALA</span>}
            {task?.submitted_done && !task?.verified_done && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 flex items-center gap-1"><Clock className="w-3 h-3"/>MENUNGGU VERIFIKASI</span>}
            {task?.verified_done && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200 flex items-center gap-1"><ShieldCheck className="w-3 h-3"/>TERVERIFIKASI</span>}
          </DialogTitle>
        </DialogHeader>

        {task && (
          <div className="space-y-4 py-2">
            {/* Meta */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-[11px] uppercase text-[#8C9096]">Divisi</div>
                <div>{div ? <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${div.color}`}>{div.short}</span> : (task.is_personal ? "—" : "-")}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase text-[#8C9096]">Jatuh Tempo</div>
                <div className={late ? "text-rose-700 font-medium" : ""}>{formatDate(task.due_date)}
                  {task.status !== "Done" && <span className="text-[11px] ml-1">({d >= 0 ? `${d} hari lagi` : `terlambat ${Math.abs(d)}h`})</span>}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase text-[#8C9096]">Ditugaskan</div>
                <div>{task.assigned_staff || "Belum ditugaskan"}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase text-[#8C9096]">Prioritas</div>
                <div><StatusBadge status={task.priority} label={TASK_PRIORITY_LABEL[task.priority]} /></div>
              </div>
              {!task.is_personal && task.booking_id && (
                <div className="col-span-2">
                  <div className="text-[11px] uppercase text-[#8C9096]">Klien / Acara</div>
                  <Link to={`/bookings/${task.booking_id}`} className="text-brand hover:underline text-sm">
                    {task.client_name || task.event_name || task.booking_code}
                  </Link>
                  <span className="text-[11px] text-[#8C9096] font-mono ml-2">{task.booking_code}</span>
                </div>
              )}
            </div>

            {/* Status quick switch */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-[11px] uppercase text-[#8C9096]">Status:</div>
              {TASK_STATUSES.filter((s) => s !== "Done").map((s) => (
                <button key={s} disabled={!canAct} onClick={() => changeStatus(s)}
                  className={`text-xs px-2.5 py-1 rounded border ${task.status === s ? "bg-[#1E2022] text-white border-[#1E2022]" : "bg-white border-[#E5E0D8] hover:bg-[#FAF6EE]"} disabled:opacity-50`}
                  data-testid={`btn-set-status-${s.replace(/\s+/g,'-').toLowerCase()}`}>
                  {TASK_STATUS_LABEL[s]}
                </button>
              ))}
            </div>

            {/* Notes */}
            {task.notes && (
              <div className="text-sm text-[#3C4045] bg-[#FAF6EE] border border-[#EAE3D6] rounded p-3">
                <div className="text-[11px] uppercase text-[#8C9096] mb-1">Catatan</div>
                {task.notes}
              </div>
            )}

            {/* Blocker banner */}
            {task.blocked && (
              <div className="border border-rose-200 bg-rose-50 rounded p-3 text-sm">
                <div className="flex items-center gap-2 font-semibold text-rose-800"><AlertTriangle className="w-4 h-4"/>Kendala terlapor</div>
                <div className="mt-1 text-rose-900">{task.blocker_note}</div>
                <div className="text-[11px] text-rose-700 mt-1">oleh {task.blocked_by} • {formatDate(task.blocked_at)}</div>
                {canAct && <Button size="sm" variant="outline" className="mt-2" onClick={doUnblock} data-testid="btn-unblock-task"><Undo2 className="w-3.5 h-3.5 mr-1"/>Tutup Kendala</Button>}
              </div>
            )}

            {/* Rejection banner */}
            {task.rejection_note && !task.verified_done && (
              <div className="border border-amber-200 bg-amber-50 rounded p-3 text-sm">
                <div className="flex items-center gap-2 font-semibold text-amber-800"><ShieldX className="w-4 h-4"/>Klaim selesai ditolak</div>
                <div className="mt-1 text-amber-900">{task.rejection_note}</div>
                <div className="text-[11px] text-amber-700 mt-1">oleh {task.rejected_by} • {formatDate(task.rejected_at)}</div>
              </div>
            )}

            {/* Checklist */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold">Sub-ceklis</div>
                <div className="text-[11px] text-[#8C9096]">{doneCount}/{checklist.length} • {pct}%</div>
              </div>
              {checklist.length > 0 && (
                <div className="w-full h-1.5 bg-[#F0EBE0] rounded mb-3 overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                </div>
              )}
              <div className="space-y-1.5">
                {checklist.map((it) => (
                  <div key={it.id} className="flex items-start gap-2 group" data-testid={`checklist-item-${it.id}`}>
                    <Checkbox checked={!!it.done} disabled={!canAct} onCheckedChange={(v) => toggleItem(it.id, !!v)} className="mt-0.5" data-testid={`checklist-toggle-${it.id}`}/>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm ${it.done ? "line-through text-[#8C9096]" : ""}`}>{it.text}</div>
                      {it.done && <div className="text-[10px] text-[#8C9096]">✓ {it.done_by} • {formatDate(it.done_at)}</div>}
                    </div>
                    {canAct && (
                      <button onClick={() => removeItem(it.id)} className="opacity-0 group-hover:opacity-100 text-rose-500 hover:text-rose-700" title="Hapus"><Trash2 className="w-3.5 h-3.5"/></button>
                    )}
                  </div>
                ))}
                {checklist.length === 0 && <div className="text-xs text-[#8C9096] italic">Belum ada item ceklis.</div>}
              </div>
              {canAct && (
                <div className="flex items-center gap-2 mt-3">
                  <Input placeholder="Tambah item ceklis…" value={newItem} onChange={(e) => setNewItem(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addItem()} className="text-sm" data-testid="input-new-checklist-item"/>
                  <Button size="sm" onClick={addItem} disabled={!newItem.trim()} className="bg-brand text-white" data-testid="btn-add-checklist-item"><Plus className="w-4 h-4"/></Button>
                </div>
              )}
            </div>

            {/* Submitted / verified meta */}
            {task.submitted_done && (
              <div className="border border-[#E5E0D8] rounded p-3 bg-white text-sm">
                <div className="text-[11px] uppercase text-[#8C9096]">Klaim selesai</div>
                <div>oleh {task.submitted_by} • {formatDate(task.submitted_at)}</div>
                {task.submit_note && <div className="italic text-[#3C4045] mt-1">"{task.submit_note}"</div>}
                {task.verified_done && <div className="text-emerald-700 text-[11px] mt-1">Diverifikasi {task.verified_by} • {formatDate(task.verified_at)}</div>}
              </div>
            )}

            {/* Action zone */}
            {canAct && !task.verified_done && (
              <div className="border-t border-[#E5E0D8] pt-4 space-y-3">
                {/* Staff submit-done (only if not already submitted) */}
                {!task.submitted_done && (
                  <div>
                    <Label className="text-xs">Catatan penyelesaian (opsional)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input value={submitNote} onChange={(e) => setSubmitNote(e.target.value)} placeholder="Contoh: sudah kirim ke klien via WA" className="text-sm" data-testid="input-submit-note"/>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={doSubmitDone} data-testid="btn-submit-done">
                        <Send className="w-3.5 h-3.5 mr-1"/>{task.is_personal ? "Tandai Selesai" : "Kirim Selesai"}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Verifier verify/reject */}
                {task.submitted_done && !task.verified_done && canVerify && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={doVerify} data-testid="btn-verify-done">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1"/>Verifikasi Selesai
                    </Button>
                    <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => setShowRejectForm((v) => !v)} data-testid="btn-open-reject">
                      <ShieldX className="w-3.5 h-3.5 mr-1"/>Tolak Klaim
                    </Button>
                    {showRejectForm && (
                      <div className="w-full mt-2 flex items-center gap-2">
                        <Input value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} placeholder="Alasan penolakan…" className="text-sm" data-testid="input-reject-note"/>
                        <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white" onClick={doReject} data-testid="btn-confirm-reject">Kirim</Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Lapor kendala */}
                {!task.is_personal && !task.blocked && (
                  <div>
                    <Button size="sm" variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-50"
                      onClick={() => setShowBlockForm((v) => !v)} data-testid="btn-open-blocker">
                      <AlertTriangle className="w-3.5 h-3.5 mr-1"/>Lapor Kendala
                    </Button>
                    {showBlockForm && (
                      <div className="mt-2 space-y-2">
                        <Textarea rows={2} value={blockerNote} onChange={(e) => setBlockerNote(e.target.value)}
                          placeholder="Jelaskan kendala yang menghambat…" className="text-sm" data-testid="input-blocker-note"/>
                        <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={doBlock} data-testid="btn-confirm-block">Kirim Laporan</Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="btn-close-task-detail">Tutup</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
